import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ScanText, PencilLine, Sparkles, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FIELDS = [
  ["ragione_sociale", "Ragione sociale *", "sm:col-span-2"],
  ["piva", "Partita IVA *", ""],
  ["codice_fiscale", "Codice fiscale", ""],
  ["indirizzo", "Sede (via, CAP, città, provincia) *", "sm:col-span-2"],
  ["telefono", "Telefono", ""],
  ["email", "Email", ""],
  ["pec", "PEC", ""],
  ["iban", "IBAN (per farti pagare)", ""],
];

const Choice = ({ icon: Icon, title, text, onClick, busy, testid, strong }) => (
  <button type="button" onClick={onClick} disabled={busy} data-testid={testid}
    className={`rounded-xl border-2 p-4 text-left transition-colors active:scale-[0.98] ${strong ? "border-primary/40 bg-primary/5 hover:border-primary" : "border-border hover:border-foreground/30"}`}>
    {busy ? <Loader2 className="w-6 h-6 animate-spin text-primary" /> : <Icon className={`w-6 h-6 ${strong ? "text-primary" : "text-muted-foreground"}`} />}
    <div className="font-semibold mt-2">{title}</div>
    <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{text}</div>
  </button>
);

// Chiede i dati dell'azienda solo quando servono (primo preventivo, fattura o DDT): visura letta dall'IA o a mano.
export default function CompanyDataDialog({ open, initial, onClose, onSaved }) {
  const [mode, setMode] = useState("choose");
  const [f, setF] = useState({});
  const [reading, setReading] = useState(false);
  const [fromVisura, setFromVisura] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    setMode("choose"); setFromVisura(false);
    setF(Object.fromEntries(FIELDS.map(([k]) => [k, initial?.[k] || ""])));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const read = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setReading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/ocr/visura", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 150000 });
      setF((x) => ({ ...x, ...Object.fromEntries(Object.entries(r.data).filter(([k, v]) => v && k in x)) }));
      setFromVisura(true); setMode("manual");
      toast.success("Visura letta: controlla i dati e conferma");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Lettura non riuscita: inserisci i dati a mano"); }
    finally { setReading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/profile", f);
      toast.success("Dati aziendali salvati");
      window.dispatchEvent(new Event("settings-updated"));
      onSaved(r.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !reading && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="company-gate-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Completa i dati della tua azienda</DialogTitle>
          <DialogDescription>Servono per intestare preventivi, fatture e DDT. Li inserisci una volta sola.</DialogDescription>
        </DialogHeader>
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={read} data-testid="company-gate-file-input" />
        {mode === "choose" ? (
          <div className="grid sm:grid-cols-2 gap-3">
            <Choice strong icon={ScanText} busy={reading} onClick={() => fileRef.current?.click()} testid="company-gate-visura-option"
              title={reading ? "Sto leggendo la visura…" : "Leggi la visura camerale"} text={reading ? "Può servire fino a un minuto." : "Carica il PDF o una foto: l'IA compila i campi per te."} />
            <Choice icon={PencilLine} onClick={() => setMode("manual")} testid="company-gate-manual-option" title="Inserisci a mano" text="Bastano Partita IVA e indirizzo della sede." />
          </div>
        ) : (
          <div className="space-y-4">
            {fromVisura && <div className="rounded-lg bg-primary/10 text-sm p-3 flex gap-2" data-testid="company-gate-visura-banner"><Sparkles className="w-4 h-4 text-primary shrink-0 mt-0.5" /> Dati letti dalla visura: controllali prima di salvare.</div>}
            <div className="grid sm:grid-cols-2 gap-3">
              {FIELDS.map(([k, label, cls]) => (
                <div key={k} className={`space-y-1.5 ${cls}`}>
                  <Label className="text-xs">{label}</Label>
                  <Input value={f[k] || ""} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))} data-testid={`company-gate-${k}-input`} />
                </div>
              ))}
            </div>
          </div>
        )}
        <DialogFooter className="gap-2 sm:justify-between">
          {mode === "manual"
            ? <Button variant="ghost" onClick={() => setMode("choose")} data-testid="company-gate-back-button"><ArrowLeft className="w-4 h-4 mr-1" /> Indietro</Button>
            : <Button variant="ghost" onClick={onClose} disabled={reading} data-testid="company-gate-later-button">Più tardi</Button>}
          {mode === "manual" && (
            <Button onClick={save} disabled={saving || !f.ragione_sociale || !f.piva || !f.indirizzo} data-testid="company-gate-save-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva e continua"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
